import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { Sidebar } from "primereact/sidebar";
import { Bars3Icon, MegaphoneIcon, ListBulletIcon } from "@heroicons/react/24/outline";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Button } from "primereact/button";
import InputText from "../../components/form-control/input-text";
import { InputTextarea } from "primereact/inputtextarea";
import apiFunctions from "../../services/api-service";
import { Select } from "antd";
import { Calendar } from "primereact/calendar";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { MultiSelect } from "primereact/multiselect";
import { useJsApiLoader } from "@react-google-maps/api";

import useToast from "../../components/common/useToast";
import AdvertisementCard from "../../components/common/AdvertisementCard";

const { Option } = Select;

declare global {
  interface Window {
    Razorpay: any;
  }
}

interface FormValues {
  company_name: string;
  short_description: string;
  long_description: string;
  whatsapp_number: string;
  mobile_number: string;
  email: string;
  website_link: string;
  city: {
    placeId?: string;
    name?: string;
    formattedAddress?: string;
    city?: string;
    state?: string;
    country?: string;
    postalCode?: string;
    street?: string;
    area?: string;
    latitude?: number;
    longitude?: number;
    types?: string[];
  } | null;
  page_name: string[];
  ad_type: string;
  amount: number;

  package_id: string;
  start_date: Date | null;
  end_date: Date | null;
}

const options = [
  { label: "All", value: "all" },
  { label: "Home Post", value: "homepost" },
  { label: "Home Story", value: "homestory" },
  { label: "MaitriMonial", value: "maitrimonial" },
  { label: "Directory", value: "users" },
  { label: "Marketplace", value: "business" },
  { label: "Bazar Bhav", value: "bazarbhav" },
  { label: "Jobs", value: "job" },
  { label: "Blood Group", value: "bloodgroup" },
  { label: "Notification", value: "notification" },
];

const validationSchema = Yup.object().shape({
  company_name: Yup.string().required("Company name required"),
  short_description: Yup.string().max(30, "Maximum 30 characters allowed"),
  long_description: Yup.string().max(500, "Maximum 500 characters allowed"),
  mobile_number: Yup.string().required("Mobile required"),
  ad_type: Yup.string().required("Ad type required"),
});

const formatDate = (date: Date | null) => {
  if (!date) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
};

const CreateAd: React.FC = () => {

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyCEW1Wc11SUHoF3sgsxEq0M1XgOqSelP5M",
    libraries: ["places"]
  });

  const { userid } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [drawerOpen, setDrawerOpen] = useState(false);

  const packageScrollRef = React.useRef<HTMLDivElement>(null);

  const [advertisementPackageOptions, setAdvertisementPackageOptions] = useState<any[]>([]);
  const [firstAdvertisementPackage, setFirstAdvertisementPackage] = useState(0);
  const [rows] = useState(10);
  const [fetchingAdvertisementPackage, setFetchingAdvertisementPackage] = useState(false);
  const [totalRecordsAdvertisementPackage, setTotalRecordsAdvertisementPackage] = useState(0);

  const cityInputRef = React.useRef<HTMLInputElement>(null);

  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<any[]>([]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isLoaded || !cityInputRef.current) return;
    const timer = setTimeout(() => {
      if (!cityInputRef.current) return;
      const autocomplete = new (window as any).google.maps.places.Autocomplete(
        cityInputRef.current,
        { types: ["(cities)"] }
      );
      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (!place.place_id) return;
        const components = place.address_components || [];
        const getComp = (type: string) =>
          components.find((c: any) => c.types.includes(type))?.long_name || "";
        formik.setFieldValue("city", {
          placeId: place.place_id,
          name: place.name,
          formattedAddress: place.formatted_address,
          city: getComp("locality") || getComp("administrative_area_level_2"),
          state: getComp("administrative_area_level_1"),
          country: getComp("country"),
          postalCode: getComp("postal_code"),
          street: getComp("route"),
          area: getComp("sublocality") || getComp("neighborhood"),
          latitude: place.geometry?.location?.lat(),
          longitude: place.geometry?.location?.lng(),
          types: place.types || [],
        });
      });
    }, 100);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  /* Load Razorpay */
  const loadRazorpay = () => {
    return new Promise<boolean>((resolve) => {

      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");

      script.src = "https://checkout.razorpay.com/v1/checkout.js";

      script.onload = () => resolve(true);

      script.onerror = () => resolve(false);

      document.body.appendChild(script);

    });
  };

  /* Handle File */
  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {

    const selectedFiles = Array.from(e.target.files || []);

    const validFiles = selectedFiles.filter(file => {

      const isImage = file.type.startsWith("image/");
      const isVideo = file.type.startsWith("video/");

      if (!isImage && !isVideo) {
        showToast({ severity: "warn", summary: "Invalid File", detail: "Only JPG, PNG, MP4 allowed", life: 3000 });
        return false;
      }

      // if (file.size > 20 * 1024 * 1024) {
      //   alert("File must be less than 20MB");
      //   return false;
      // }

      return true;

    });

    setFiles(validFiles);

    const previewItems = validFiles.map(file => ({
      url: URL.createObjectURL(file),
      file,
      type: file.type.startsWith("video") ? "video" : "image"
    }));

    setPreviews(previewItems);

  };

  /* Submit */
  const formik = useFormik<FormValues>({
    initialValues: {
      company_name: "",
      short_description: "",
      long_description: "",
      whatsapp_number: "",
      mobile_number: "",
      email: "",
      website_link: "",
      city: null,
      page_name: [],
      ad_type: "image",
      amount: 0,
      package_id: "",
      start_date: null,
      end_date: null,
    },
    validationSchema,
    onSubmit: async (values) => {

      try {
        setLoading(true);

        if (values.amount <= 0) {
          showToast({ severity: "warn", summary: "Package Required", detail: "Please select any package before pay and publish", life: 3000 });
          setLoading(false);
          return;
        }

        const razorLoaded = await loadRazorpay();

        if (!razorLoaded) {
          showToast({ severity: "error", summary: "Error", detail: "Razorpay failed to load", life: 3000 });
          setLoading(false);
          return;
        }

        const orderRes = await axios.post(
          `${process.env.REACT_APP_API_URL}/advertisement`,
          {
            userid,
            amount: values.amount
          }
        );

        const order = orderRes.data.order;

        const options = {

          key: "rzp_live_SxPm6WoawVwEun",

          amount: order.amount,

          currency: order.currency,

          order_id: order.id,

          name: "Sagar Samaj",

          description: "Promote your business",

          image: "https://sagarsamaj.in/static/media/logosmall.155d8b602123092d7a45.png",

          handler: async (response: any) => {

            try {
              setLoading(true);

              const formData = new FormData();

              formData.append("userid", userid ?? "");
              formData.append("company_name", values.company_name);
              formData.append("long_description", values.long_description);
              formData.append("short_description", values.short_description);
              formData.append(
                "city",
                JSON.stringify(values.city)
              );
              formData.append("email", values.email);
              formData.append("website_link", values.website_link);
              formData.append("whatsapp_number", values.whatsapp_number);
              formData.append("mobile_number", values.mobile_number);
              formData.append("page_name", JSON.stringify(values.page_name) ?? []);
              formData.append("ad_type", values.ad_type);
              formData.append("start_date", formatDate(values.start_date));
              formData.append("end_date", formatDate(values.end_date));

              files.forEach(file => {
                formData.append("media", file);
              });

              formData.append("razorpay_order_id", response.razorpay_order_id);
              formData.append("razorpay_payment_id", response.razorpay_payment_id);
              formData.append("razorpay_signature", response.razorpay_signature);

              await axios.post(
                `${process.env.REACT_APP_API_URL}/advertisement/verify/advertisement`,
                formData
              );

              showToast({ severity: "success", summary: "Created", detail: "Advertisement created successfully!", life: 3000 });
            } catch (err) {
              console.error(err);
              showToast({ severity: "error", summary: "Error", detail: "Payment verification failed", life: 4000 });
            } finally {
              setLoading(false); // ONLY HERE
            }
          },
          prefill: {
            name: values.company_name,
          },

          theme: {
            color: "#2563eb"
          },

          // User closed/exited the Razorpay checkout without paying —
          // without this, `loading` stays true forever and the button is
          // stuck on "Processing...".
          modal: {
            ondismiss: () => setLoading(false),
          }

        };

        const rzp = new window.Razorpay(options);

        rzp.on("payment.failed", () => {
          showToast({ severity: "error", summary: "Payment Failed", detail: "Payment could not be completed. Please try again.", life: 4000 });
          setLoading(false);
        });

        rzp.open();

      } catch (err) {

        console.log(err);

        showToast({ severity: "error", summary: "Error", detail: "Error creating advertisement", life: 4000 });
        setLoading(false);

      }

    }
  });

  const handleSectionChange = (e: any) => {

    let value = e.value;

    const allOption = "all";

    const normalOptions = options
      .filter(o => o.value !== allOption)
      .map(o => o.value);

    // If ALL selected
    if (value.includes(allOption)) {

      formik.setFieldValue("page_name", [allOption, ...normalOptions]);
      return;

    }

    // If user selected all manually
    const allSelected = normalOptions.every(v => value.includes(v));

    if (allSelected) {

      formik.setFieldValue("page_name", [allOption, ...normalOptions]);

    } else {

      formik.setFieldValue(
        "page_name",
        value.filter((v: string) => v !== allOption)
      );

    }

  };

  const fetchAdvertismentPackage = async (reset = false) => {

    if (fetchingAdvertisementPackage) return;

    setFetchingAdvertisementPackage(true);

    const page = reset ? 1 : Math.floor(firstAdvertisementPackage / rows) + 1;

    try {

      let payload = {
        section: formik.values.page_name || [],
        type: formik.values.ad_type || "",
        cityId: formik.values.city?.city || ""
      }

      // let payload = {
      //   section: formik.values.page_name || [],
      //   type: formik.values.ad_type || "",
      //   cityId: formik.values.city?.placeId || "",
      //   latitude: formik.values.city?.latitude || "",
      //   longitude: formik.values.city?.longitude || "",
      //   country: formik.values.city?.country || "",
      //   state: formik.values.city?.state || ""
      // }

      const response = await apiFunctions.post(
        `/advertisementpackage/dropdown?page=${page}&limit=${rows}`, payload
      );

      const newItems = response?.data?.data || [];
      const total = response?.data?.totalRecords || 0;

      if (reset) {
        setAdvertisementPackageOptions(newItems);
        setFirstAdvertisementPackage(rows);
      } else {
        setAdvertisementPackageOptions(prev => [...prev, ...newItems]);
        setFirstAdvertisementPackage(prev => prev + rows);
      }

      setTotalRecordsAdvertisementPackage(total);

    } finally {

      setFetchingAdvertisementPackage(false);

    }

  };

  useEffect(() => {

    console.log("Trigger package fetch", {
      page_name: formik.values.page_name,
      ad_type: formik.values.ad_type,
      city: formik.values.city
    });

    if (!formik.values.page_name || !formik.values.ad_type) return;

    setAdvertisementPackageOptions([]);
    setFirstAdvertisementPackage(0);

    fetchAdvertismentPackage(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.page_name,
    formik.values.ad_type,
    formik.values.city
  ]);
  const handlePackageSelect = (pkg: any) => {

    const start = new Date();

    const days = Number(pkg.days); // ✅ convert string → number

    const end = new Date(start);

    end.setDate(start.getDate() + days);

    formik.setFieldValue("package_id", pkg._id);

    formik.setFieldValue("amount", pkg.price);

    formik.setFieldValue("start_date", start);

    formik.setFieldValue("end_date", end);

  };

  const handlePackageScroll = () => {

    const container = packageScrollRef.current;

    if (!container) return;

    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;

    const isNearEnd =
      scrollLeft + clientWidth >= scrollWidth - 50;

    if (
      isNearEnd &&
      !fetchingAdvertisementPackage &&
      advertisementPackageOptions.length <
      totalRecordsAdvertisementPackage
    ) {
      fetchAdvertismentPackage(false);
    }

  };

  useEffect(() => {
    return () => {
      previews.forEach(p => URL.revokeObjectURL(p.url));
    };
  }, [previews]);

  useEffect(() => {
    if (!loading) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "F5" ||
        (e.ctrlKey && e.key.toLowerCase() === "r")
      ) {
        e.preventDefault();
      }
    };

    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
    };

    // Push state to block back button
    window.history.pushState(null, "", window.location.href);

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [loading]);

  return (
    <div className="min-h-screen bg-gray-100" >

      {/* Header */}
      < div className="bg-blue-600 text-white py-3 px-3 sm:px-6 shadow" >

        <div className="flex items-center gap-2 max-w-5xl mx-auto">

          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="shrink-0 p-2 rounded-full bg-white/10 hover:bg-white/20 cursor-pointer flex items-center justify-center"
            aria-label="Menu"
          >
            <Bars3Icon className="w-6 h-6 sm:w-8 sm:h-8 text-white shrink-0" />
          </button>

          <div className="flex-1 min-w-0 text-center">
            <h1 className="text-base sm:text-3xl font-bold leading-tight break-words">
              Promote Your Business <span className="whitespace-nowrap">🚀</span>
            </h1>

            <p className="opacity-90 text-[11px] sm:text-base mt-0.5">
              Reach thousands of customers instantly
            </p>
          </div>

          {/* spacer to balance the menu button so the title stays centered */}
          <div className="w-10 sm:w-12 shrink-0" aria-hidden="true" />

        </div>

      </div >

      {/* Side Drawer */}
      <Sidebar visible={drawerOpen} onHide={() => setDrawerOpen(false)} position="left" className="w-72 max-w-full">
        <h3 className="text-lg font-semibold mb-4">Menu</h3>
        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={() => {
              setDrawerOpen(false);
              formik.resetForm();
              setFiles([]);
              setPreviews([]);
              navigate(`/advertisement/${userid}`);
            }}
            className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-gray-100 cursor-pointer text-left"
          >
            <MegaphoneIcon className="w-6 h-6 text-blue-600" />
            <span className="font-medium text-gray-800">Add Advertisement</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setDrawerOpen(false);
              navigate(`/advertisement/${userid}/my`);
            }}
            className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-gray-100 cursor-pointer text-left"
          >
            <ListBulletIcon className="w-6 h-6 text-blue-600" />
            <span className="font-medium text-gray-800">View All Your Advertisement</span>
          </button>
        </div>
      </Sidebar>

      {/* Main */}
      <div className="max-w-5xl mx-auto px-3 py-4 sm:p-6 grid md:grid-cols-2 gap-4 sm:gap-6 items-start">

        {/* LEFT FORM */}
        <form
          onSubmit={formik.handleSubmit}
          className="bg-white p-3 sm:p-6 rounded-xl shadow w-full min-w-0"
        >

          <h2 className="text-xl font-semibold mb-4">
            Advertisement Details
          </h2>

          <InputText
            id="company_name"
            label="Company Name"
            modelValue={formik.values.company_name}
            onUpdateModelValue={(val) =>
              formik.setFieldValue("company_name", val)
            }
            validation={
              formik.errors.company_name &&
              formik.touched.company_name && {
                $error: true,
                required: {
                  $invalid: true,
                  $message: formik.errors.company_name
                }
              }
            }
          />

          <div className="field mt-3">
            <label className="form-label">Short Description</label>

            <InputTextarea
              value={formik.values.short_description}
              onChange={(e) =>
                formik.setFieldValue("short_description", e.target.value)
              }
              rows={2}
              className={`w-full resize-y overflow-y-auto ${formik.touched.short_description &&
                formik.errors.short_description
                ? "p-invalid"
                : ""
                }`}
              maxLength={30}
            />

            <div className="flex justify-between text-xs mt-1">
              <span className="p-error">
                {formik.touched.short_description &&
                  formik.errors.short_description}
              </span>

              <span className="text-gray-500">
                {formik.values.short_description.length}/30
              </span>
            </div>
          </div>

          <div className="field mt-3">
            <label className="form-label">Long Description</label>

            <InputTextarea
              value={formik.values.long_description}
              onChange={(e) =>
                formik.setFieldValue("long_description", e.target.value)
              }
              rows={4}
              className="w-full resize-y overflow-y-auto"
              maxLength={500}
            />

            <div className="text-right text-xs text-gray-500 mt-1">
              {formik.values.long_description.length}/500
            </div>
          </div>

          {/* <InputText
            id="city"
            label="City"
            sectionClass="field mt-3"
            modelValue={formik.values.city}
            onUpdateModelValue={(val) =>
              formik.setFieldValue("city", val)
            }
          /> */}

          <div className="field mt-3">
            <label htmlFor="cityname" className="form-label">
              Targeted City
            </label>
            <div className="relative">
              <input
                ref={cityInputRef}
                id="cityname"
                type="text"
                placeholder="Search City"
                defaultValue={formik.values.city?.formattedAddress || formik.values.city?.name || ""}
                onChange={(e) => {
                  if (!e.target.value) formik.setFieldValue("city", null);
                }}
                className="w-full border border-gray-300 rounded px-3 pr-8 text-sm focus:outline-none focus:border-blue-500"
                style={{ height: "42px" }}
              />
              {formik.values.city && (
                <button
                  type="button"
                  onClick={() => {
                    formik.setFieldValue("city", null);
                    if (cityInputRef.current) cityInputRef.current.value = "";
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none"
                >
                  ×
                </button>
              )}
            </div>
            {formik.touched.city && typeof formik.errors.city === "string" && (
              <span id="city-help" className="p-error text-left mt-2">
                {formik.errors.city}
              </span>
            )}
          </div>

          <div className="field mt-3">
            <label className="form-label">Mobile</label>
            <PhoneInput
              country="in"
              value={formik.values.mobile_number}
              onChange={(val) => formik.setFieldValue("mobile_number", `+${val}`)}
              inputStyle={{ width: "100%", height: "42px" }}
              containerStyle={{ width: "100%" }}
            />
            {formik.touched.mobile_number && formik.errors.mobile_number && (
              <small className="p-error">{formik.errors.mobile_number}</small>
            )}
          </div>

          <div className="field mt-3">
            <label className="form-label">Whatsapp</label>
            <PhoneInput
              country="in"
              value={formik.values.whatsapp_number}
              onChange={(val) => formik.setFieldValue("whatsapp_number", `+${val}`)}
              inputStyle={{ width: "100%", height: "42px" }}
              containerStyle={{ width: "100%" }}
            />
          </div>

          <InputText
            id="email"
            label="Email"
            sectionClass="field mt-3"
            modelValue={formik.values.email}
            onUpdateModelValue={(val) =>
              formik.setFieldValue("email", val)
            }
          />

          <InputText
            id="website_link"
            label="Website Link"
            sectionClass="field mt-3"
            modelValue={formik.values.website_link}
            onUpdateModelValue={(val) =>
              formik.setFieldValue("website_link", val)
            }
          />

          <div className="mb-4 mt-3">

            <label className="form-label block mb-1">Advertisement Type</label>

            <Select
              value={formik.values.ad_type}
              style={{ width: "100%", height: "42px" }}
              onChange={(val) =>
                formik.setFieldValue("ad_type", val)
              }
            >
              <Option value="image">Banner</Option>
              <Option value="video">Video</Option>
            </Select>
            {formik.touched.ad_type && typeof formik.errors.ad_type === "string" && (
              <span id="type-help" className="p-error text-left mt-2">
                {formik.errors.ad_type}
              </span>
            )}
          </div>

          {/* <div className="mb-4 mt-2">
            <label className="mb-2">Section</label>
            <Select
              value={formik.values.page_name}
              style={{ width: "100%", height: "42px" }}
              onChange={(val) =>
                formik.setFieldValue("page_name", val)
              }
            >
              <Option value="all">All</Option>
              <Option value="homepost">Home Post</Option>
              <Option value="homestory">Home Story</Option>
              <Option value="maintrimonial">MaitriMonial</Option>
              <Option value="users">Directory</Option>
              <Option value="business">Marketplace</Option>
              <Option value="bazarbhav">Bazar Bhav</Option>
              <Option value="job">Jobs</Option>
              <Option value="bloodgroup">Blood Group</Option>
            </Select>
          </div> */}

          <div className="mb-4 mt-3">

            <label className="form-label block mb-1">Section</label>

            <MultiSelect
              value={formik.values.page_name}
              options={options}
              optionLabel="label"
              optionValue="value"
              onChange={handleSectionChange}
              placeholder="Select Sections"
              display="chip"
              maxSelectedLabels={2}
              className="
    w-full
    min-h-[44px]
    text-sm
  "
              panelClassName="text-sm"
              optionDisabled={(option) =>
                formik.values.page_name.includes("all") &&
                option.value !== "all"
              }
              pt={{
                root: {
                  className:
                    "w-full rounded-xl overflow-hidden"
                },

                labelContainer: {
                  className:
                    "flex flex-wrap gap-1 py-1 max-w-full overflow-hidden"
                },

                token: {
                  className:
                    "max-w-full text-xs sm:text-sm px-2 py-1 break-all whitespace-normal"
                },

                label: {
                  className:
                    "text-sm break-all whitespace-normal"
                },

                trigger: {
                  className:
                    "w-10 flex items-center justify-center flex-shrink-0"
                },

                panel: {
                  className:
                    "max-w-[95vw]"
                },

                item: {
                  className:
                    "text-sm py-2 break-all whitespace-normal"
                }
              }}
            />

          </div>

          {advertisementPackageOptions && advertisementPackageOptions.length > 0 && (<div className="mt-4">

            <label className="font-semibold text-lg sm:text-xl block mb-3">
              Advertisement Packages
            </label>

            <div ref={packageScrollRef}
              onScroll={handlePackageScroll}
              className="
    flex
    gap-5
    overflow-x-auto
    pb-3
    snap-x snap-mandatory
    scrollbar-thin scrollbar-thumb-gray-300
  ">

              {advertisementPackageOptions.map(pkg => {

                const selected =
                  formik.values.package_id === pkg._id;

                const badgeText =
                  pkg.bestforyou
                    ? "Best For You"
                    : pkg.badgelabel
                      ? pkg.badgelabel
                      : null;


                return (

                  <div
                    key={pkg._id}
                    onClick={() => handlePackageSelect(pkg)}
                    className={`min-w-[160px] max-w-[160px] sm:min-w-[240px] sm:max-w-[240px] snap-center flex-shrink-0 rounded-2xl border p-3 sm:p-5 cursor-pointer transition-all duration-300 shadow-sm hover:shadow-lg hover:-translate-y-1 flex flex-col justify-between relative ${selected ? "bg-blue-50 border-blue-500 shadow-md" : "bg-white border-gray-200"}`}
                  >
                    {badgeText && (<div className=" absolute top-3 left-[-8px] bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold px-3 py-1 rounded-r-lg shadow-md "> {badgeText} </div>)}

                    {/* Package Name */}
                    <div className={`${badgeText ? "mt-[20px]" : ""}`}>

                      <div className="
              font-semibold
              text-lg
              text-gray-800
              leading-tight
            ">
                        {pkg.name}
                      </div>

                      {/* Price */}
                      <div className="
              text-blue-600
              text-2xl
              font-bold
              mt-2
            ">
                        ₹{pkg.price}
                      </div>

                      {/* Duration */}
                      <div className="
              text-gray-500
              text-sm
              mt-1
            ">
                        {pkg.days} Days
                      </div>

                    </div>

                    {/* Button */}
                    <Button
                      type="button"
                      label={selected ? "Selected" : "Select"}
                      className={`mt-5 w-full rounded-lg font-semibold
              ${selected
                          ? "bg-green-500 border-green-500 text-white"
                          : "bg-blue-500 border-blue-500 text-white"
                        }
        `}
                    />

                  </div>

                );

              })}
            </div>

          </div>)}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div className="flex flex-col gap-2">
              <label className="form-label">Start Date</label>
              <Calendar
                value={formik.values.start_date}
                onChange={(e) =>
                  formik.setFieldValue("start_date", e.value)
                }
                showIcon
                dateFormat="dd/mm/yy"
                className="w-full"
              />
              {formik.touched.start_date && formik.errors.start_date && (
                <small className="p-error">{formik.errors.start_date}</small>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="form-label">End Date</label>
              <Calendar
                value={formik.values.end_date}
                onChange={(e) =>
                  formik.setFieldValue("end_date", e.value)
                }
                showIcon
                dateFormat="dd/mm/yy"
                className="w-full"
              />
              {formik.touched.end_date && formik.errors.end_date && (
                <small className="p-error">{formik.errors.end_date}</small>
              )}
            </div>
          </div>

          {/* FILE */}
          <label className="cursor-pointer block mt-4">

            <div className="
  border-2 border-dashed border-blue-400
  rounded-xl
  p-4 sm:p-6
  text-center
  hover:bg-blue-50
  transition
">

              <div className="text-blue-600 font-semibold">
                Upload Advertisement Media
              </div>

              <div className="text-xs sm:text-sm text-gray-400">
                Images (JPG, PNG) or Video (MP4) • Recommended 1080×1920 • Max 20MB
              </div>

            </div>

            <input
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleFile}
              className="hidden"
            />

          </label>

          {/* PRICE */}
          <div className="text-lg font-bold mb-4 mt-2">

            Price: ₹{formik.values.amount}

          </div>

          <Button
            label={loading ? "Processing..." : "Pay & Publish"}
            className="prime-button btn-login w-full"
            type="submit"
          ></Button>

        </form >

        {/* RIGHT PREVIEW */}
        <div className="bg-white p-3 sm:p-6 rounded-xl shadow md:sticky md:top-4 w-full min-w-0">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-700">
              Preview
            </h2>
            <span className="text-xs font-normal bg-gray-100 px-2 py-1 rounded text-gray-500 uppercase tracking-wider">9:16 Vertical</span>
          </div>

          <div className="flex justify-center">
            <div className="relative w-full max-w-[320px] aspect-[9/16] bg-neutral-900 rounded-[2rem] border-[6px] border-neutral-800 shadow-2xl overflow-hidden ring-4 ring-neutral-200">
              <div className="absolute inset-0 overflow-hidden">
                <AdvertisementCard
                  companyName={formik.values.company_name}
                  media={previews.map((p) => ({ type: p.type, url: p.url }))}
                  shortDescription={formik.values.short_description}
                  whatsappNumber={formik.values.whatsapp_number}
                  mobileNumber={formik.values.mobile_number}
                  websiteLink={formik.values.website_link}
                />
              </div>
            </div>
          </div>
        </div >

      </div >

    </div >

  );

};

export default CreateAd;